import clsx from 'clsx'
import { css, html, nothing, unsafeCSS } from 'lit'
import { customElement, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'

import { translate } from '@/localization/localization'

import { InputBaseElement } from '../input/input-base-element'
import style from './password-input.scss?inline'

/**
 * `dap-ds-password-input`
 * @summary A password input a field for entering a password.
 * @element dap-ds-password-input
 * @title - Password input
 *
 * @property {string} label - The label of the password input.
 * @property {string} placeholder - The placeholder of the password input.
 * @property {string} description - The description of the password input.
 * @property {string} tooltip - The tooltip of the password input.
 * @property {'top' | 'right' | 'bottom' | 'left'} tooltipPlacement - The tooltip placement of the input.
 * @property {string} status - The status of the password input. Can be `success` or `error`.
 * @property {'xs' | 'sm' | 'sm'} size - The size of the password input. Default is `sm`.
 * @property {string} name - The name of the password input.
 * @property {string} value - The value of the password input.
 * @property {boolean} disabled - The disabled state of the password input. Default is false.
 * @property {boolean} required - The required state of the password input. Default is false.
 * @property {boolean} readonly - The readonly state of the password input. Default is false.
 * @property {boolean} autofocus - The autofocus state of the password input. Default is false.
 * @property {string} feedback - The feedback of the input.
 * @property {'negative' | 'positive' | 'warning'} feedbackType - The feedback type of the input. Can be `negative`, `positive`, or `warning`.
 * @property {boolean} loading - The loading state of the password input. Default is false.
 * @property {boolean} optional - The optional state of the password input.
 * @property {string} optionalLabel - The optional label of the password input.
 * @property {boolean} subtle - The weight of the label. Default is `false`
 *
 * @event {{ value: string }} dds-change - Fired when the input value changes.
 * @event {{ value: string }} dds-input - Fired when the input value changes.
 * @event {{ value: string, originalEvent: Event }} dds-keydown - Fired when a key is pressed down.
 * @event {{ void }} dds-blur - Fired when the input loses focus.
 * @event {{ void }} dds-focus - Emitted when the input gains focus.
 *
 * @cssproperty --dds-input-height - The height of the input. Default is var(--dds-spacing-1000).
 * @cssproperty --dds-input-padding - The padding of the input. Default is var(--dds-spacing-300).
 * @cssproperty --dds-input-font-size - The font size of the input. Default is var(--dds-font-base).
 * @cssproperty --dds-input-border-color - The border color of the input. Default is var(--dds-border-neutral-base).
 * @cssproperty --dds-input-background-color - The background color of the input. Default is var(--dds-fields-background-default).
 * @cssproperty --dds-input-text-color - The text color of the input. Default is var(--dds-text-neutral-base).
 * @cssproperty --dds-input-border-radius - The border radius of the input. Default is var(--dds-radius-base).
 * @cssproperty --dds-input-border-width - The border width of the input. Default is var(--dds-border-width-base).
 * @cssproperty --dds-input-disabled-background - The background color of the disabled input. Default is var(--dds-fields-background-disabled).
 * @cssproperty --dds-input-disabled-text - The text color of the disabled input. Default is var(--dds-text-neutral-disabled).
 * @cssproperty --dds-input-readonly-background - The background color of the readonly input. Default is var(--dds-fields-background-read-only).
 * @cssproperty --dds-input-readonly-text - The text color of the readonly input. Default is var(--dds-text-neutral-subtle).
 * @cssproperty --dds-input-success-border - The border color of the success input. Default is var(--dds-border-positive-base).
 * @cssproperty --dds-input-error-border - The border color of the error input. Default is var(--dds-border-negative-base).
 * @cssproperty --dds-input-addon-background - The background color of the input addon. Default is var(--dds-background-ui-neutral-enabled).
 * @cssproperty --dds-input-addon-success-background - The background color of the success input addon. Default is var(--dds-background-ui-positive-enabled).
 * @cssproperty --dds-input-addon-error-background - The background color of the error input addon. Default is var(--dds-background-ui-negative-enabled).
 *
 * @csspart postfix - The postfix of the password input.
 * @csspart password-input - The password input.
 * @csspart password-input--visible - The visible password input.
 * @csspart icon - The icon of the password input.
 * @csspart icon-base - The base of the icon.
 */
@customElement('dap-ds-password-input')
export default class DapDSPasswordInput extends InputBaseElement {
  @state() passwordVisibile: boolean = false

  static readonly styles = css`
    ${unsafeCSS(InputBaseElement.styles)}
    ${unsafeCSS(style)}
  `
  constructor() {
    super()
    this.type = 'password'
  }

  private _toggleVisibility() {
    this.passwordVisibile = !this.passwordVisibile
    this.type = this.passwordVisibile ? 'text' : 'password'
  }

  private getIconSize(size: string) {
    if (size === 'sm') {
      return 'md'
    }
    return size
  }

  render() {
    return this.renderInput(
      html` <span
        part="postfix"
        class=${clsx('input__addon-postfix password-input', {
          'password-input--visible': this.passwordVisibile,
        })}>
        <dap-ds-stack direction="row" slot="postfix">
          ${this.loading
            ? html`<dap-ds-spinner size="lg"></dap-ds-spinner>`
            : nothing}
          <dap-ds-icon-button
            part="icon"
            exportparts="base:icon-base"
            size=${ifDefined(this.getIconSize(this.size)) as 'lg' | 'md' | 'sm'}
            role="switch"
            arialabel="${this.passwordVisibile
              ? translate('hide')
              : translate('show')}"
            aria-checked=${this.passwordVisibile === true ? 'true' : 'false'}
            .disabled=${this.disabled}
            @click=${this._toggleVisibility}>
            <dap-ds-icon name="eye-line" class="input__icon-show"></dap-ds-icon>
            <dap-ds-icon
              name="eye-off-line"
              class="input__icon-hide"></dap-ds-icon>
          </dap-ds-icon-button>
        </dap-ds-stack>
      </span>`,
    )
  }
}
